/**
 * Immutable cell array manipulation functions for SQL Notebooks.
 *
 * All functions return new arrays — no mutation.
 */

import { v4 as uuidv4 } from 'uuid';
import type { NotebookCell, NotebookCodeCell, NotebookMarkdownCell, NotebookCellType } from '../types/notebookTypes';
import { createCodeCell, createJavaScriptCell, createMarkdownCell } from '../types/notebookTypes';

/** Insert a cell at a given index. Clamps to [0, cells.length]. */
export function insertCellAt(cells: NotebookCell[], index: number, cell: NotebookCell): NotebookCell[] {
  const clamped = Math.max(0, Math.min(index, cells.length));
  const next = [...cells];
  next.splice(clamped, 0, cell);
  return next;
}

/** Create a cell from a cell type string. */
function createCellFromType(id: string, cellType: NotebookCellType): NotebookCell {
  if (cellType === 'javascript') return createJavaScriptCell(id);
  if (cellType === 'markdown') return createMarkdownCell(id);
  return createCodeCell(id);
}

/** Insert a new cell after the cell with the given id. Returns unchanged array if id not found. */
export function insertCellAfter(
  cells: NotebookCell[],
  afterCellId: string,
  cellType: NotebookCellType
): { cells: NotebookCell[]; newCellId: string } {
  const idx = cells.findIndex((c) => c.id === afterCellId);
  if (idx === -1) return { cells, newCellId: '' };
  const id = uuidv4();
  const cell = createCellFromType(id, cellType);
  return { cells: insertCellAt(cells, idx + 1, cell), newCellId: id };
}

/** Append a new cell at the end. */
export function appendCell(
  cells: NotebookCell[],
  cellType: NotebookCellType
): { cells: NotebookCell[]; newCellId: string } {
  const id = uuidv4();
  const cell = createCellFromType(id, cellType);
  return { cells: [...cells, cell], newCellId: id };
}

/** Delete cell by id. Returns unchanged array if not found. */
export function deleteCell(cells: NotebookCell[], cellId: string): NotebookCell[] {
  return cells.filter((c) => c.id !== cellId);
}

/** Move cell up by one position. No-op if already first. */
export function moveCellUp(cells: NotebookCell[], cellId: string): NotebookCell[] {
  const idx = cells.findIndex((c) => c.id === cellId);
  if (idx <= 0) return cells;
  const next = [...cells];
  [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
  return next;
}

/** Move cell down by one position. No-op if already last. */
export function moveCellDown(cells: NotebookCell[], cellId: string): NotebookCell[] {
  const idx = cells.findIndex((c) => c.id === cellId);
  if (idx === -1 || idx >= cells.length - 1) return cells;
  const next = [...cells];
  [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
  return next;
}

/** Update the source of a specific cell. */
export function updateCellSource(cells: NotebookCell[], cellId: string, source: string): NotebookCell[] {
  return cells.map((c) => (c.id === cellId ? { ...c, source } : c));
}

/** Convert any cell to markdown. Preserves source, clears outputs. */
export function convertToMarkdown(cell: NotebookCell): NotebookMarkdownCell {
  return createMarkdownCell(cell.id, cell.source, true);
}

/** Convert any cell to code. Preserves source. */
export function convertToCode(cell: NotebookCell): NotebookCodeCell {
  return createCodeCell(cell.id, cell.source);
}

/** Convert any cell to JavaScript. Preserves source. */
export function convertToJavaScript(cell: NotebookCell): NotebookCell {
  return createJavaScriptCell(cell.id, cell.source);
}

/** Check if a cell has meaningful content (non-whitespace). */
export function cellHasContent(cell: NotebookCell): boolean {
  return cell.source.trim().length > 0;
}

/** Get the index of a cell by id, or -1 if not found. */
export function getCellIndex(cells: NotebookCell[], cellId: string): number {
  return cells.findIndex((c) => c.id === cellId);
}

/** Insert a new cell before the cell with the given id. Returns unchanged array if id not found. */
export function insertCellBefore(
  cells: NotebookCell[],
  beforeCellId: string,
  cellType: NotebookCellType
): { cells: NotebookCell[]; newCellId: string } {
  const idx = cells.findIndex((c) => c.id === beforeCellId);
  if (idx === -1) return { cells, newCellId: '' };
  const id = uuidv4();
  const cell = createCellFromType(id, cellType);
  return { cells: insertCellAt(cells, idx, cell), newCellId: id };
}

/** Duplicate a cell below the original. Executable cells get outputs cleared; markdown cells start in edit mode. */
export function duplicateCell(
  cells: NotebookCell[],
  cellId: string
): { cells: NotebookCell[]; newCellId: string } {
  const idx = cells.findIndex((c) => c.id === cellId);
  if (idx === -1) return { cells, newCellId: '' };
  const original = cells[idx];
  const newId = uuidv4();
  const duplicate: NotebookCell =
    original.cellType === 'javascript'
      ? createJavaScriptCell(newId, original.source)
      : original.cellType === 'code'
        ? createCodeCell(newId, original.source)
        : createMarkdownCell(newId, original.source, true);
  return { cells: insertCellAt(cells, idx + 1, duplicate), newCellId: newId };
}

/** Reorder cells by moving the cell at activeId to the position of overId (for drag-and-drop). */
export function reorderCells(cells: NotebookCell[], activeId: string, overId: string): NotebookCell[] {
  const oldIndex = cells.findIndex((c) => c.id === activeId);
  const newIndex = cells.findIndex((c) => c.id === overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return cells;
  const next = [...cells];
  const [removed] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, removed);
  return next;
}
